/**
 * Scroll reveals for the homepage. Server HTML is fully visible. This runs
 * after hydration: sections already on screen (or above it) are marked shown
 * first, then `data-reveal-ready` on <html> lets the CSS hide only the
 * sections still below the fold until they scroll in. Visitors who prefer
 * reduced motion keep the page exactly as the server sent it.
 */
export const REVEAL_SELECTOR = "[data-reveal]";

/** How a section was shown: at once on load, or by scrolling to it. */
export type RevealMode = "instant" | "scroll";

const markRevealed = (element: HTMLElement, mode: RevealMode) => {
  element.dataset.revealed = mode;
};

export const armRevealOnScroll = (): (() => void) => {
  const root = document.documentElement;
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  if (prefersReducedMotion || !("IntersectionObserver" in window)) {
    return () => {};
  }

  const pending: HTMLElement[] = [];
  for (const element of document.querySelectorAll<HTMLElement>(
    REVEAL_SELECTOR,
  )) {
    if (element.dataset.revealed) continue;
    if (element.getBoundingClientRect().top < window.innerHeight) {
      markRevealed(element, "instant");
    } else {
      pending.push(element);
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (entry.target instanceof HTMLElement) {
          markRevealed(entry.target, "scroll");
        }
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: "0px 0px -8% 0px" },
  );
  for (const element of pending) observer.observe(element);

  // Set last, in the same task as the marks above, so nothing on screen hides.
  root.dataset.revealReady = "";

  return () => {
    observer.disconnect();
    delete root.dataset.revealReady;
  };
};
